import { ChangeEvent, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@libs/zapi-ui/shadcn";
import { AppButton, AppTextarea, InputLabel } from "@libs/zapi-ui/custom";
import { PaperclipIcon, XIcon } from "@phosphor-icons/react";
import $api from "@/zapi-frontend/shared/api/axios";

type DeficiencyType =
  | "inconsistency"
  | "redundancy"
  | "repetition"
  | "tone_of_voice"
  | "translation_quality"
  | "low_confidence_claim"
  | "missing_citation"
  | "hallucination"
  | "other";

type DeficiencySeverity = "critical" | "major" | "minor";

export type TFlagTarget =
  | { kind: "memoChapter"; projectId: number; chapterId: number | null }
  | { kind: "caseBrief"; caseBriefId: number; projectId?: number | null }
  | { kind: "projectsListChat"; organizationId: number };

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const TYPE_OPTIONS: { value: DeficiencyType; label: string }[] = [
  { value: "low_confidence_claim", label: "Low Confidence Claim" },
  { value: "missing_citation", label: "Missing Citation" },
  { value: "hallucination", label: "Hallucination" },
  { value: "inconsistency", label: "Inconsistency" },
  { value: "redundancy", label: "Redundancy" },
  { value: "repetition", label: "Repetition" },
  { value: "tone_of_voice", label: "Tone of Voice" },
  { value: "translation_quality", label: "Translation Quality" },
  { value: "other", label: "Other" }
];

const SEVERITY_OPTIONS: { value: DeficiencySeverity; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" }
];

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  target: TFlagTarget;
  quote: string;
  contextLabel?: string;
};

export function FlagDeficiencyDialog({ isOpen, onOpenChange, target, quote, contextLabel }: Props) {
  const [type, setType] = useState<DeficiencyType>("low_confidence_claim");
  const [severity, setSeverity] = useState<DeficiencySeverity>("major");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState<{ url: string; name: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadProjectId =
    target.kind === "memoChapter"
      ? target.projectId
      : target.kind === "caseBrief"
        ? (target.projectId ?? null)
        : null;

  const reset = () => {
    setType("low_confidence_claim");
    setSeverity("major");
    setDescription("");
    setAttachment(null);
    setIsUploading(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadProjectId) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error("File is too large (max 20MB).");
      return;
    }
    setIsUploading(true);
    try {
      const res = await $api.post<{ data: { url: string } }>(
        `/projects/${uploadProjectId}/public-assets`,
        { file },
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = res.data?.data?.url;
      if (!url) throw new Error("Missing upload url");
      setAttachment({ url, name: file.name });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      toast.error(status === 403 ? "You need edit access on this project to attach files." : "Failed to upload file.");
    } finally {
      setIsUploading(false);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const trimmedDescription = description.trim();
      const shared = {
        type,
        severity,
        description: contextLabel ? `[${contextLabel}] ${trimmedDescription}` : trimmedDescription,
        attachmentUrl: attachment?.url,
        attachmentName: attachment?.name
      };
      if (target.kind === "caseBrief") {
        return $api.post("/deficiencies/case-brief", {
          caseBriefId: target.caseBriefId,
          projectId: target.projectId ?? undefined,
          quote,
          ...shared
        });
      }
      if (target.kind === "projectsListChat") {
        return $api.post("/deficiencies/projects-list-chat", {
          organizationId: target.organizationId,
          quote,
          ...shared
        });
      }
      return $api.post("/deficiencies", {
        projectId: target.projectId,
        chapterAId: target.chapterId,
        chapterAQuote: quote,
        ...shared
      });
    },
    onSuccess: () => {
      toast.success("Deficiency flagged for review.");
      close();
    },
    onError: () => {
      toast.error("Failed to flag deficiency.");
    }
  });

  const handleSubmit = () => {
    if (target.kind === "memoChapter" && !target.chapterId) {
      toast.error("No active chapter to flag against.");
      return;
    }
    if (!quote.trim()) {
      toast.error("Select some text to flag.");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-[480px]" data-toolbar="true" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="text-[16px] font-medium">Flag a deficiency</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-1.5">
            <InputLabel>{contextLabel ?? "Selected text"}</InputLabel>
            <blockquote className="border-l-2 border-innoveraZinc-200 pl-3 max-h-24 overflow-y-auto">
              <p className="text-[12px] text-innoveraZinc-600 italic leading-[18px] wrap-anywhere">{quote}</p>
            </blockquote>
          </div>

          <div className="flex gap-3">
            <div className="flex flex-col gap-2 flex-1">
              <InputLabel>Type</InputLabel>
              <Select value={type} onValueChange={(v) => setType(v as DeficiencyType)}>
                <SelectTrigger data-testid="flag-deficiency-type-select" className="h-8 text-[14px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-1100">
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`flag-deficiency-type-${o.value}-item`}
                      className="text-[14px]"
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2 flex-1">
              <InputLabel>Severity</InputLabel>
              <Select value={severity} onValueChange={(v) => setSeverity(v as DeficiencySeverity)}>
                <SelectTrigger data-testid="flag-deficiency-severity-select" className="h-8 text-[14px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-1100">
                  {SEVERITY_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`flag-deficiency-severity-${o.value}-item`}
                      className="text-[14px]"
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <AppTextarea
            data-testid="flag-deficiency-description-input"
            label="What's wrong?"
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue with the selected text"
            className="min-h-[72px] resize-y"
          />

          {uploadProjectId && (
            <div className="flex flex-col gap-1.5">
              <InputLabel>Attachment (optional)</InputLabel>
              <input
                ref={fileInputRef}
                type="file"
                data-testid="flag-deficiency-file-input"
                className="hidden"
                onChange={handleFileChange}
              />
              {attachment ? (
                <div className="flex items-center gap-2 rounded-md border border-innoveraZinc-150 px-3 py-2">
                  <PaperclipIcon size={14} className="text-innoveraZinc-500 shrink-0" />
                  <span className="text-[12px] text-zinc-700 truncate flex-1">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    data-testid="flag-deficiency-remove-attachment-button"
                    className="text-innoveraZinc-400 hover:text-zinc-700 shrink-0"
                    aria-label="Remove attachment"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              ) : (
                <AppButton
                  data-testid="flag-deficiency-attach-file-button"
                  appearance="outlined"
                  color="zinc150"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  isLoading={isUploading}
                  startAdornment={<PaperclipIcon size={14} />}
                  className="self-start text-[14px] leading-[13px]"
                >
                  {isUploading ? "Uploading..." : "Attach a file"}
                </AppButton>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <AppButton
            data-testid="flag-deficiency-cancel-button"
            appearance="outlined"
            color="zinc150"
            onClick={close}
            disabled={mutation.isPending}
          >
            Cancel
          </AppButton>
          <AppButton
            data-testid="flag-deficiency-submit-button"
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            disabled={isUploading}
          >
            Flag deficiency
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
